import { Alert, Box, Button, FormControlLabel, InputAdornment, Radio, Stack, Switch, TextField, Typography } from "@mui/material";
import CustomPopover, { usePopover } from "../custom-popover";
import useDatePreset, { presetValues } from "src/hooks/use-date-preset";
import { useEffect, useState } from "react";
import DateRagePicker from "../custom-date-range-picker/custom-date-range-picker";
import { TimeStep } from "src/context/reducers/attribution-settings";
import { useDispatch, useSelector } from "react-redux";
import { useLocales } from "src/locales";
import { useResponsive } from "src/hooks/use-responsive";
import { differenceInDays } from 'date-fns';
import { enqueueSnackbar } from "notistack";
import { setDatesForcedByActivation, setFilterCompare, setFilterDailyDisabled, setFilterTimestep } from 'src/context/reducers/filter-settings/filterSlice';
import Iconify from "../iconify";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

interface DatePreset {
    displayName: string;
    start: Date;
    end: Date;
    step: string;
}

interface Props {
    features?: { time_step: boolean };
    startDate: Date;
    compareStartDate: any;
    compareEndDate: any;

    endDate: Date;
    timeStep?: TimeStep;
    selected: string;
    preset: string;
    onChangeStartDate: (date: Date | null) => void;
    onChangeEndDate: (date: Date | null) => void;

    onChangeCompareStartDate: (date: Date | null) => void;
    onChangeCompareEndDate: (date: Date | null) => void;

    onChangePreset: (value: string) => void;
    onChangeTimeStep?: (value: TimeStep) => void;
}

interface Options {
    step: string;
    preset: string;
    selected?: "custom" | "preset";
    start?: Date;
    end?: Date;
}

// --- Laisse bien ce hook pour rétrocompatibilité (anciennes pages) ---
export const useDateRangeSelect = (options: Options) => {
    const datePreset = useDatePreset(options.preset);
    const [startDate, setStartDate] = useState<Date | null>(options.start || null);
    const [endDate, setEndDate] = useState<Date | null>(options.end || null);
    const [selected, setSelected] = useState<"custom" | "preset">(options.selected || "preset");
    const [timeStep, setTimeStep] = useState<TimeStep>(options.step as TimeStep || presetValues[options.preset].step);

    const onChangePreset = (value: string) => {
        datePreset.setPreset(value);
        setTimeStep(presetValues[value].step);
        setSelected("preset");
    };

    const onChangeStartDate = (date: Date | null) => {
        setStartDate(date);
        setSelected("custom");
    };

    const onChangeEndDate = (date: Date | null) => {
        setEndDate(date);
        setSelected("custom");
    };

    const onChangeTimeStep = (value: TimeStep) => {
        setTimeStep(value);
    };

    return {
        preset: selected === "preset" ? datePreset : null,
        start: startDate,
        end: endDate,
        step: timeStep,
        selected,
        onChangeEndDate,
        onChangeStartDate,
        onChangePreset,
        onChangeTimeStep,
    };
};

// --- Ton nouveau composant principal ---
export default function DateRangeSelect(props: Props) {
    const { t } = useLocales();
    const calendar = usePopover();
    const dispatch = useDispatch();
    const [preset, setPreset] = useState<string>(props.preset);
    const [startDate, setStartDate] = useState<Date | null>(props.startDate);
    const filterStore = useSelector((state: any) => state.filters);
    const workspace = useCurrentWorkspace();
    const [compareStartDate, setCompareStartDate] = useState<Date | null>(props.compareStartDate);
    const [compareEndDate, setCompareEndDate] = useState<Date | null>(props.compareEndDate);

    const [endDate, setEndDate] = useState<Date | null>(props.endDate);
    const [timeStep, setTimeStep] = useState<TimeStep | null>(null);
    const [selected, setSelected] = useState<"custom" | "preset">("preset");

    const [enableComparison, setEnableComparison] = useState(filterStore?.compare || false);
    const [userSelectedTimeStep, setUserSelectedTimeStep] = useState<TimeStep | null>(null); // Ajout
    const lgUp = useResponsive('up', 'lg');
    const startDateStore = useSelector((state: any) => state.filters.start);
    const endDateStore = useSelector((state: any) => state.filters.end);

useEffect(() => {
    const isDemo = workspace?.name?.toLowerCase().includes("demo");
    const currentPath = location?.pathname;

    if (isDemo && currentPath === "/optimization/overview") return;

    if (!userSelectedTimeStep && startDateStore && endDateStore) {
        const days = differenceInDays(new Date(endDateStore), new Date(startDateStore));
        let nextTimeStep: TimeStep = "date_day";

        if (days <= 14) nextTimeStep = "date_day";
        else if (days <= 90) nextTimeStep = "year_week";
        else nextTimeStep = "year_month";

        setTimeStep(nextTimeStep);
        dispatch(setFilterTimestep(nextTimeStep));
    } else if (userSelectedTimeStep) {
        setTimeStep(userSelectedTimeStep);
    }
}, [endDateStore, startDateStore, userSelectedTimeStep, workspace, location?.pathname]);


    useEffect(() => {
        if (!calendar.open) {
            setEnableComparison(filterStore?.compare || false);
        }
    }, [calendar.open, filterStore?.compare]);

    const onChangeStartDate = (date: Date | null) => {
        setStartDate(date);
        setSelected("custom");
    }

    const onChangeCompareStartDate = (date: Date | null) => {
        setCompareStartDate(date);
        setSelected("custom");
    }

    const onChangeCompareEndDate = (date: Date | null) => {
        setCompareEndDate(date);
        setSelected("custom");
    }

    const onChangeEndDate = (date: Date | null) => {
        setEndDate(date);
        setSelected("custom");
    }

    const handleEnableComparisonChange = () => {
        setEnableComparison(!enableComparison);
    };

    const handleTimeStepChange = (value: TimeStep) => {
        setUserSelectedTimeStep(value);
        setTimeStep(value);
    };

    const apply = () => {
        // Calculer le nombre de jours dans la période de comparaison et de sélection
        const selectedDays = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
        const compareDays = compareStartDate && compareEndDate ? differenceInDays(compareEndDate, compareStartDate) : 0;
        dispatch(setDatesForcedByActivation(false)); // 🧠 on bloque les autres effets

        // Check if the periods match
        if (enableComparison && selectedDays !== compareDays) {
            enqueueSnackbar('The selected and comparison \n date ranges do not match \n in the number of days.', { variant: 'warning' });
        }

        if (selected === 'preset') {
            const { start, end } = presetValues[preset];
            props.onChangeStartDate(start);
            props.onChangeEndDate(end);
        } else {
            props.onChangeEndDate(endDate);
            props.onChangeStartDate(startDate);

            props.onChangeCompareStartDate(compareStartDate);
            props.onChangeCompareEndDate(compareEndDate);
        }

        if (props.features?.time_step === true && props.onChangeTimeStep && timeStep) {
            props.onChangeTimeStep(timeStep);
            dispatch(setFilterTimestep(timeStep));
        }


        dispatch(setFilterCompare(enableComparison));
        calendar.onClose();
    };

    const selectedDays = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
    const compareDays = compareStartDate && compareEndDate ? differenceInDays(compareEndDate, compareStartDate) : 0;

    useEffect(() => {
        const bing = filterStore?.platforms.length === 1 && filterStore?.platforms.includes('Bing')
        const multiple = filterStore?.platforms.length > 1 && filterStore?.platforms.includes('Bing')
        const all = filterStore?.platforms.length === 0

        if (filterStore.timestep === 'date_day' && bing) {
            setTimeStep('year_week')
            dispatch(setFilterTimestep('year_week'))
            dispatch(setFilterDailyDisabled(bing))
        } else if (filterStore.timestep !== 'date_day' && bing) {
            dispatch(setFilterDailyDisabled(bing))
        }
        else if ((all || multiple) && window.location.pathname != "/brand-overview") {
            dispatch(setFilterDailyDisabled(false))
        }
    }, [filterStore])

    return (
        <>
            <TextField
                size="small"
                onClick={calendar.onOpen}
                sx={{
                    borderRadius: 1,
                    width: { sm: "100%", xs: "100%", lg: 230 },
                }}
                value={props.selected}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify icon={"solar:calendar-line-duotone"} width={20} sx={{ position: 'relative', top: -1 }} />
                        </InputAdornment>
                    ),
                }}
            />
            <CustomPopover
                sx={{ pt: 2, px: 1, pb: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
                open={calendar.open}
                onClose={calendar.onClose}
            >

                <Stack sx={{ width: '100%', position: 'relative' }} display={'flex'} flexDirection={'column'}>
                    {enableComparison && selectedDays !== null && compareDays !== null && selectedDays !== compareDays && compareDays !== 29 && (
                        <Alert severity="warning" variant="outlined">
                            The selected and comparison date ranges do not match in the number of days.
                        </Alert>
                    )}

                    <DateRagePicker
                        onClose={calendar.onClose}
                        variant="calendar"
                        startDate={selected === "custom" ? startDate : presetValues[preset].start}
                        endDate={selected === "custom" ? endDate : presetValues[preset].end}
                        onChangeEndDate={onChangeEndDate}
                        onChangeStartDate={onChangeStartDate}
                        onChangeCompareStartDate={onChangeCompareStartDate}
                        enableComparison={enableComparison}
                        onChangeCompareEndDate={onChangeCompareEndDate}
                    />

                    <Box
                        sx={{
                            ml: 1,
                            mt: 2,
                            position: lgUp ? 'absolute' : 'relative',
                            top: lgUp ? (enableComparison && selectedDays !== null && compareDays !== null && selectedDays !== compareDays && compareDays !== 29 ? 330 : 280) : 0,
                            left: lgUp ? 17 : 0
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size={lgUp ? "small" : "medium"}
                                    checked={enableComparison}
                                    onChange={handleEnableComparisonChange}
                                />
                            }
                            label={<Box sx={{ fontSize: lgUp ? 12 : 14 }}>{t('dashboard.filters.enable_comparison')}</Box>}
                        />

                    </Box>

                    <Stack flexDirection={'row'} height={'100%'} spacing={2} mb={2} mt={2} ml={1}>
                        {props.features?.time_step === true ? (
                            <Box display={location?.pathname === "/dash/brand-wayback" ? "none" : "block"}>
                                <Typography fontSize={15}>{t('dashboard.filters.group_data')}: </Typography>
                                {/* Les 3 boutons radios, rien de sélectionné si auto */}
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={userSelectedTimeStep === 'date_day'}
                                            onChange={() => handleTimeStepChange('date_day')}
                                            disabled={filterStore.dailyDisabled}
                                        />
                                    }
                                    label={t('dashboard.filters.daily')}
                                />
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={userSelectedTimeStep === 'year_week'}
                                            onChange={() => handleTimeStepChange('year_week')}
                                        />
                                    }
                                    label={t('dashboard.filters.weekly')}
                                />
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={userSelectedTimeStep === 'year_month'}
                                            onChange={() => handleTimeStepChange('year_month')}
                                        />
                                    }
                                    label={t('dashboard.filters.monthly')}
                                />
                            </Box>
                        ) : (
                            ''
                        )}
                        <Box flexGrow={1} />
                        <Stack alignSelf={'end'} direction={'row'} spacing={2}>
                            <Button
                                sx={{ maxHeight: '30px', my: 'auto' }}
                                variant="outlined"
                                color="inherit"
                                onClick={() => {
                                    calendar.onClose();
                                    setEnableComparison(filterStore?.compare || false);
                                }}
                            >
                                {t('dashboard.global.cancel')}
                            </Button>
                            <Button
                                sx={{ maxHeight: '30px', my: 'auto' }}
                                color="primary"
                                variant="contained"
                                onClick={apply}
                            >
                                {t('dashboard.filters.ok')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </CustomPopover>
        </>
    )
}
